import { type ReadonlySignal, type Signal, signal } from "@preact/signals-core";
import type { TableMusicState } from "./table_music";

export type SoundLabels = {
	title: string;
	panel_title: string;
	tabs_label: string;
	effects_title: string;
	stop: string;
	volume: string;
	ambiences: string;
	musics: string;
	credits: string;
	credits_href?: string;
	help: string;
	help_href?: string;
	close: string;
	failed: string;
	your_volume: string;
	tap_to_listen: string;
	tap_to_enable: string;
	stay_silent: string;
	plans_href?: string;
	uploads: UploadLabels;
};

// The owner's own files in the panel: `%{count}` and `%{limit}` the tally, `%{minutes}` and `%{seconds}` a length; the send form's words fill `%{file}`, `%{size}`, `%{sent}`, `%{total}` and `%{length}`.
export type UploadLabels = {
	own_music: string;
	own_effects: string;
	of_music: string;
	of_effects: string;
	count: string;
	for_subscribers: string;
	edit: string;
	done: string;
	save: string;
	delete: string;
	cancel: string;
	name: string;
	view_plans: string;
	music_limit: string;
	effects_limit: string;
	music_full: string;
	effects_full: string;
	free: string;
	lapsed_music: string;
	lapsed_effects: string;
	confirm_music: string;
	confirm_effect: string;
	playing_warning: string;
	length_minutes: string;
	length_seconds: string;
	send_music: string;
	send_effect: string;
	back: string;
	choose: string;
	drop: string;
	choose_other: string;
	detail: string;
	placeholder_music: string;
	placeholder_effect: string;
	hint_music: string;
	hint_effect: string;
	rights_music: string;
	rights_effect: string;
	send: string;
	progress: string;
	err_type: string;
	err_size_music: string;
	err_size_effect: string;
	err_unreadable: string;
	err_stopped: string;
};

export type SoundItem = { key: string; name: string; author: string; seconds: number };
// An ambience group has no mood; each mood of music is a group of its own.
export type SoundGroup = { kind: "ambience" | "music"; mood: string | null; label: string; sounds: SoundItem[] };

// `icon` is the slug of a file in images/icons/sound.
export type SoundEffect = { key: string; name: string; icon: string };

export type Upload = { id: string; kind: "music" | "effect"; name: string; seconds: number | null; url: string };
export type Uploads = { rows: Upload[]; owner: boolean; plan: "paid" | "lapsed" | "free"; url: string };

// The send form's state: the form itself (null while the list shows), the request in flight and the newest file choice. It belongs to
// one board's controller, which clears it on disconnect.
export type Draft = {
	kind: Upload["kind"];
	file: File | null;
	seconds: number | null;
	name: string;
	rights: boolean;
	alert: string | null;
	progress: { sent: number; total: number } | null;
};
export type SendSession = { draft: Signal<Draft | null>; abort: (() => void) | null; pick: number };

export type TableState = { track: string | null; volume: number };

// What the Som panel's island and the chip ask of the board they sit on. The board's `map-sound` controller sets it when it connects
// and clears it when it goes; `master` is there on the master's own board alone.
export type SoundBridge = {
	labels: SoundLabels;
	groups: SoundGroup[];
	effects: SoundEffect[];
	// Every upload this board knows of, by id: the master's list and whatever the cable taught it.
	uploads: ReadonlySignal<Map<string, Upload>>;
	// This device's player: what plays, whether the browser still blocks it, the viewer's own volume and whether they turned it off.
	state: ReadonlySignal<TableMusicState>;
	unlock: () => void;
	setVolume: (volume: number) => void;
	setOff: (off: boolean) => void;
	master?: {
		// What the table plays and the level everyone starts from.
		table: ReadonlySignal<TableState>;
		play: (track: string | null) => void;
		fire: (key: string) => void;
		setTableVolume: (volume: number) => void;
		close: () => void;
		// The owner's files, as the panel lists them; null when the board was given none.
		files: ReadonlySignal<Uploads | null>;
		refreshFiles: () => Promise<void>;
		rename: (id: string, name: string) => Promise<void>;
		remove: (id: string) => Promise<void>;
		// A file the owner just sent: at the top of the list, and known to the player.
		add: (row: Upload) => void;
		send: SendSession;
	};
};

export const soundBridge = signal<SoundBridge | null>(null);

export const nameOf = (groups: SoundGroup[], uploads: Map<string, Upload>, key: string | null) =>
	groups.flatMap((group) => group.sounds).find((sound) => sound.key === key)?.name ?? (key ? uploads.get(key)?.name : undefined) ?? "";
